import { Column } from '@ant-design/charts';
import { useCubeQuery } from '@cubejs-client/react';
import { VizState } from '@threatadvisor/react-components';
import { formatDate, useTranslation } from '@threatadvisor/react-utils';
import { Typography } from 'antd';
import React, { ReactElement, useEffect, useId, useMemo } from 'react';

export type EventVolumeByWeekProps = {
  clientUid: string;
  reportStart: string;
  reportEnd: string;
};

export default function EventVolumeByWeek(props: EventVolumeByWeekProps): ReactElement {
  const { clientUid, reportStart, reportEnd } = props;

  const { t } = useTranslation(['translation', 'reports']);

  const chartConfig: VizState = {
    chartType: 'column',
    pivotConfig: {
      x: [`security_event.createdDate.week`],
      y: ['security_event.source', 'measures'],
      fillMissingDates: true
      //joinDateRange: false
    },
    query: {
      measures: ['security_event.count'],
      order: {
        'security_event.count': 'desc'
      },
      dimensions: ['security_event.source'],
      timeDimensions: [
        {
          dimension: 'security_event.createdDate',
          granularity: 'week',
          dateRange: [reportStart, reportEnd]
        }
      ],
      filters: [
        {
          member: 'security_event.clientUid',
          operator: 'equals',
          values: [clientUid]
        }
      ]
    }
  };

  const { resultSet, isLoading } = useCubeQuery(chartConfig.query!);

  const volByWeek: { x: string; y: number; series: string }[] | null = useMemo(() => {
    if (resultSet) {
      const data: { x: string; y: number; series: string }[] = [];
      const results = resultSet.chartPivot(chartConfig.pivotConfig);
      resultSet.seriesNames(chartConfig.pivotConfig).forEach(({ key }) => {
        data.push(
          ...results.map(result => ({
            x: formatDate(new Date(result.x)),
            y: result[key],
            series: key.replace(',security_event.count', '')
          }))
        );
      });
      return data;
    }
    return null;
  }, [resultSet]);

  const myId = useId();
  useEffect(() => {
    if (!window['chartRenders']) window['chartRenders'] = {};
    window['chartRenders'][myId] = 'Rendering';
    return () => {
      window['chartRenders'] && delete window['chartRenders'][myId];
    };
  }, []);

  function getDomains(): string[] {
    const domains: string[] = [];
    if (volByWeek?.find(res => res.series === 'Abnormal Security'))
      domains.push('Abnormal Security');
    if (volByWeek?.find(res => res.series === 'Email')) domains.push('Email');
    if (volByWeek?.find(res => res.series === 'Proofpoint')) domains.push('Proofpoint');
    if (volByWeek?.find(res => res.series === 'Zscaler')) domains.push('Zscaler');
    if (volByWeek?.find(res => res.series === 'CrowdStrike')) domains.push('CrowdStrike');
    if (volByWeek?.find(res => res.series === 'InsightIDR')) domains.push('InsightIDR');

    return domains;
  }
  function getRange() {
    const range: string[] = [];
    //console.log('getrange');
    if (volByWeek?.find(res => res.series === 'Abnormal Security')) range.push('#800020');
    if (volByWeek?.find(res => res.series === 'Email')) range.push('#FF5D00');
    if (volByWeek?.find(res => res.series === 'Proofpoint')) range.push('#ffc104');
    if (volByWeek?.find(res => res.series === 'Zscaler')) range.push('#69e05c');
    if (volByWeek?.find(res => res.series === 'CrowdStrike')) range.push('#4572c5');
    if (volByWeek?.find(res => res.series === 'InsightIDR')) range.push('#501371');

    return range;
  }

  return (
    <>
      <h2 className="text-uppercase">Event Volume By Week</h2>

      <figure>
        {' '}
        {!isLoading && volByWeek ? (
          <Column
            onReady={plot => {
              plot.chart.on('beforerender', args => {
                console.debug('Before EventVolumeByWeek', myId);
                window['chartRenders'] = {
                  ...window['chartRenders'],
                  [myId]: 'Rendering (before EventVolumeByWeek)'
                };
              });

              plot.chart.on('afterrender', args => {
                if (window['chartRenders']) window['chartRenders'][myId] = 'Done';
              });
            }}
            {...{
              isGroup: true,
              data: volByWeek,
              seriesField: 'series',
              xField: 'x',
              yField: 'y',
              colorField: 'series',
              label: {
                text: val => (val.y === 0 ? '' : val.y),
                textBaseline: 'bottom'
              },
              scale: {
                color: {
                  domain: getDomains(),
                  range: getRange()
                }
              },
              theme: {
                type: 'threatadvisor'
              }
            }}
          />
        ) : (
          <></>
        )}
        <figcaption>
          <Typography.Text type="secondary">
            Shows trending data for event volume received by week.
          </Typography.Text>
        </figcaption>
      </figure>
    </>
  );
}
